import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/AuthContext';
import { useBalance } from '@/contexts/BalanceContext';
import { useModalQueue } from '@/contexts/ModalQueueContext';
import { useToast } from '@/hooks/use-toast';

interface PendingRedemption {
  id: string;
  amount: number;
  created_at: string;
  status: string;
}

interface PendingRedemptionContextType {
  pendingRedemptions: PendingRedemption[];
  pendingCount: number;
  totalPendingAmount: number;
  isLoading: boolean;
  showPopup: boolean;
  isCancelling: boolean;
  isLowBalanceTrigger: boolean;
  dismissPopup: () => void;
  cancelRedemption: (requestId: string) => Promise<boolean>;
  refreshPendingRedemptions: () => Promise<void>;
}

const PendingRedemptionContext = createContext<PendingRedemptionContextType | undefined>(undefined);

export const usePendingRedemption = () => {
  const context = useContext(PendingRedemptionContext);
  if (!context) {
    throw new Error('usePendingRedemption must be used within a PendingRedemptionProvider');
  }
  return context;
};

const LOW_BALANCE_THRESHOLD = 1; // Show popup when SC balance drops below this

export const PendingRedemptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { balance, refreshBalance } = useBalance();
  const { requestShow, notifyDismissed, shouldShow } = useModalQueue();
  const { toast } = useToast();
  
  const [pendingRedemptions, setPendingRedemptions] = useState<PendingRedemption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isLowBalanceTrigger, setIsLowBalanceTrigger] = useState(false);
  const hasFetchedRef = useRef(false);
  const hasRequestedShowRef = useRef(false);
  const hasShownLowBalancePopupRef = useRef(false);
  const previousBalanceRef = useRef<number | null>(null);

  const pendingCount = pendingRedemptions.length;
  const totalPendingAmount = pendingRedemptions.reduce((sum, r) => sum + (parseFloat(String(r.amount)) || 0), 0);

  // Use modal queue to determine visibility
  const showPopup = shouldShow('PENDING_REDEMPTION');

  // Calculate total SC balance
  const totalScBalance = balance 
    ? balance.sc_bonus + balance.sc_purchased + balance.sc_redeemable 
    : null;

  const refreshPendingRedemptions = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const response = await apiClient.get('/redemption/history?status=pending&page=1&limit=10');
      if (response?.success && response.requestHistory) {
        const pending = response.requestHistory.filter((r: any) => r.status === 'pending');
        setPendingRedemptions(pending);
        
        // Request to show popup if there are pending redemptions (will queue behind other modals)
        if (pending.length > 0 && !hasRequestedShowRef.current) {
          hasRequestedShowRef.current = true;
          setIsLowBalanceTrigger(false);
          requestShow('PENDING_REDEMPTION');
        }
      }
    } catch {
      // Silently fail - not critical
    } finally {
      setIsLoading(false);
    }
  }, [user, requestShow]);

  // Fetch pending redemptions on mount when user is logged in
  useEffect(() => {
    if (user && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      refreshPendingRedemptions();
    }
    
    // Reset flags when user logs out
    if (!user) {
      hasFetchedRef.current = false;
      hasRequestedShowRef.current = false;
      setPendingRedemptions([]);
      hasShownLowBalancePopupRef.current = false;
      previousBalanceRef.current = null;
    }
  }, [user, refreshPendingRedemptions]);

  // Watch for low balance - show popup automatically when balance drops below threshold
  useEffect(() => {
    if (!user || totalScBalance === null || pendingCount === 0) {
      return;
    }

    const previousBalance = previousBalanceRef.current;
    previousBalanceRef.current = totalScBalance;

    // Only trigger if:
    // 1. Balance dropped below threshold
    // 2. Previous balance was above threshold (so we detect the transition)
    // 3. We haven't already shown the low balance popup this session
    // 4. There are pending redemptions
    const balanceJustDroppedBelowThreshold = 
      totalScBalance < LOW_BALANCE_THRESHOLD && 
      previousBalance !== null && 
      previousBalance >= LOW_BALANCE_THRESHOLD;

    if (balanceJustDroppedBelowThreshold && !hasShownLowBalancePopupRef.current && pendingCount > 0) {
      hasShownLowBalancePopupRef.current = true;
      setIsLowBalanceTrigger(true);
      requestShow('PENDING_REDEMPTION');
    }
  }, [user, totalScBalance, pendingCount, requestShow]);

  // Reset low balance popup flag when balance goes back above threshold
  useEffect(() => {
    if (totalScBalance !== null && totalScBalance >= LOW_BALANCE_THRESHOLD * 2) {
      // Reset when balance is comfortably above threshold (2x)
      // This allows the popup to show again if they run low again
      hasShownLowBalancePopupRef.current = false;
    }
  }, [totalScBalance]);

  const dismissPopup = useCallback(() => {
    setIsLowBalanceTrigger(false);
    notifyDismissed('PENDING_REDEMPTION');
  }, [notifyDismissed]);

  const cancelRedemption = useCallback(async (requestId: string): Promise<boolean> => {
    setIsCancelling(true);
    try {
      const data = await apiClient.put(`/redemption/requests/${requestId}/cancel`);
      if (data?.success) {
        toast({
          title: "Request Cancelled",
          description: data.message || "Your redemption request has been cancelled and SC credited back.",
          variant: "success",
          duration: 4000,
        });
        
        // Refresh balance and pending redemptions
        await Promise.all([
          refreshBalance(),
          refreshPendingRedemptions(),
        ]);
        
        // Close popup if no more pending redemptions
        const remaining = pendingRedemptions.filter(r => String(r.id) !== String(requestId));
        if (remaining.length === 0) {
          notifyDismissed('PENDING_REDEMPTION');
        }
        
        return true;
      }
      return false;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to cancel request. Please try again.",
        variant: "destructive",
        duration: 4000,
      });
      return false;
    } finally {
      setIsCancelling(false);
    }
  }, [toast, refreshBalance, refreshPendingRedemptions, pendingRedemptions, notifyDismissed]);

  return (
    <PendingRedemptionContext.Provider
      value={{
        pendingRedemptions,
        pendingCount,
        totalPendingAmount,
        isLoading,
        showPopup,
        isCancelling,
        isLowBalanceTrigger,
        dismissPopup,
        cancelRedemption,
        refreshPendingRedemptions,
      }}
    >
      {children}
    </PendingRedemptionContext.Provider>
  );
};
